/**
 * The postseason chapter's screen guides and guided tours — ADR 9044.
 *
 * One entry per route of `./manifest.ts`, keyed by its `path` exactly as declared, in the
 * manifest's order: a guide (five parts and a tour) or an exemption with its reason.
 * `tools/screen-guides.mjs` fails `census:features` on a route without an entry, a key unwritten in
 * any locale, or a tour anchor no element carries.
 *
 * The texts (`locale/<locale>/postseason.json`, `postseason.guide.*`) were written from each screen's code —
 * what it reads, what each figure means, what each control writes — not from its comments. A
 * screen change that changes what a planner sees changes its guide in the same PR.
 */

import type { GuideTable } from '../guides';

export const POSTSEASON_GUIDES: GuideTable = {
  'review': {
    purposeKey: 'postseason.guide.review.purpose',
    readingKey: 'postseason.guide.review.reading',
    actionsKey: 'postseason.guide.review.actions',
    statesKey: 'postseason.guide.review.states',
    sourceKey: 'postseason.guide.review.source',
    tour: [
      { anchor: 'postseason-season-picker', titleKey: 'postseason.guide.review.tour.season.title', bodyKey: 'postseason.guide.review.tour.season.body' },
      { anchor: 'postseason-review-basis', titleKey: 'postseason.guide.review.tour.basis.title', bodyKey: 'postseason.guide.review.tour.basis.body' },
      { anchor: 'postseason-review-glance', titleKey: 'postseason.guide.review.tour.glance.title', bodyKey: 'postseason.guide.review.tour.glance.body' },
      { anchor: 'postseason-review-table', titleKey: 'postseason.guide.review.tour.table.title', bodyKey: 'postseason.guide.review.tour.table.body' },
      { anchor: 'postseason-review-run', titleKey: 'postseason.guide.review.tour.run.title', bodyKey: 'postseason.guide.review.tour.run.body' },
    ],
  },
  'lost-sales': {
    purposeKey: 'postseason.guide.lostSales.purpose',
    readingKey: 'postseason.guide.lostSales.reading',
    actionsKey: 'postseason.guide.lostSales.actions',
    statesKey: 'postseason.guide.lostSales.states',
    sourceKey: 'postseason.guide.lostSales.source',
    tour: [
      { anchor: 'page-status', titleKey: 'postseason.guide.lostSales.tour.pageStatus.title', bodyKey: 'postseason.guide.lostSales.tour.pageStatus.body' },
      { anchor: 'postseason-season-picker', titleKey: 'postseason.guide.lostSales.tour.season.title', bodyKey: 'postseason.guide.lostSales.tour.season.body' },
      { anchor: 'headline', titleKey: 'postseason.guide.lostSales.tour.headline.title', bodyKey: 'postseason.guide.lostSales.tour.headline.body' },
      { anchor: 'kpi-row', titleKey: 'postseason.guide.lostSales.tour.kpiRow.title', bodyKey: 'postseason.guide.lostSales.tour.kpiRow.body' },
      { anchor: 'postseason-lost-sales-bridge', titleKey: 'postseason.guide.lostSales.tour.bridge.title', bodyKey: 'postseason.guide.lostSales.tour.bridge.body' },
      { anchor: 'constants-band', titleKey: 'postseason.guide.lostSales.tour.constants.title', bodyKey: 'postseason.guide.lostSales.tour.constants.body' },
      { anchor: 'postseason-lost-sales-census', titleKey: 'postseason.guide.lostSales.tour.census.title', bodyKey: 'postseason.guide.lostSales.tour.census.body' },
      { anchor: 'provenance', titleKey: 'postseason.guide.lostSales.tour.provenance.title', bodyKey: 'postseason.guide.lostSales.tour.provenance.body' },
    ],
  },
  'lost-sales-potential': {
    purposeKey: 'postseason.guide.lostSalesPotential.purpose',
    readingKey: 'postseason.guide.lostSalesPotential.reading',
    actionsKey: 'postseason.guide.lostSalesPotential.actions',
    statesKey: 'postseason.guide.lostSalesPotential.states',
    sourceKey: 'postseason.guide.lostSalesPotential.source',
    tour: [
      { anchor: 'page-status', titleKey: 'postseason.guide.lostSalesPotential.tour.pageStatus.title', bodyKey: 'postseason.guide.lostSalesPotential.tour.pageStatus.body' },
      { anchor: 'postseason-season-picker', titleKey: 'postseason.guide.lostSalesPotential.tour.season.title', bodyKey: 'postseason.guide.lostSalesPotential.tour.season.body' },
      { anchor: 'headline', titleKey: 'postseason.guide.lostSalesPotential.tour.headline.title', bodyKey: 'postseason.guide.lostSalesPotential.tour.headline.body' },
      { anchor: 'kpi-row', titleKey: 'postseason.guide.lostSalesPotential.tour.kpiRow.title', bodyKey: 'postseason.guide.lostSalesPotential.tour.kpiRow.body' },
      { anchor: 'postseason-lost-sales-potential-chart', titleKey: 'postseason.guide.lostSalesPotential.tour.chart.title', bodyKey: 'postseason.guide.lostSalesPotential.tour.chart.body' },
      { anchor: 'constants-band', titleKey: 'postseason.guide.lostSalesPotential.tour.constants.title', bodyKey: 'postseason.guide.lostSalesPotential.tour.constants.body' },
      { anchor: 'postseason-lost-sales-potential-table', titleKey: 'postseason.guide.lostSalesPotential.tour.table.title', bodyKey: 'postseason.guide.lostSalesPotential.tour.table.body' },
    ],
  },
  'lost-sales-panel': {
    purposeKey: 'postseason.guide.lostSalesPanel.purpose',
    readingKey: 'postseason.guide.lostSalesPanel.reading',
    actionsKey: 'postseason.guide.lostSalesPanel.actions',
    statesKey: 'postseason.guide.lostSalesPanel.states',
    sourceKey: 'postseason.guide.lostSalesPanel.source',
    tour: [
      { anchor: 'postseason-lost-sales-panel-choose', titleKey: 'postseason.guide.lostSalesPanel.tour.chooseCut.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.chooseCut.body' },
      { anchor: 'postseason-lost-sales-panel-coverage', titleKey: 'postseason.guide.lostSalesPanel.tour.coverage.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.coverage.body' },
      { anchor: 'ranked-bar', titleKey: 'postseason.guide.lostSalesPanel.tour.rankBar.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.rankBar.body' },
      { anchor: 'postseason-lost-sales-panel-filters', titleKey: 'postseason.guide.lostSalesPanel.tour.filters.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.filters.body' },
      { anchor: 'headline', titleKey: 'postseason.guide.lostSalesPanel.tour.headline.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.headline.body' },
      { anchor: 'kpi-row', titleKey: 'postseason.guide.lostSalesPanel.tour.kpiRow.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.kpiRow.body' },
      { anchor: 'postseason-lost-sales-panel-map', titleKey: 'postseason.guide.lostSalesPanel.tour.map.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.map.body' },
      { anchor: 'postseason-lost-sales-panel-cells', titleKey: 'postseason.guide.lostSalesPanel.tour.cells.title', bodyKey: 'postseason.guide.lostSalesPanel.tour.cells.body' },
    ],
  },
  'accuracy': { exempt: 'Not offered in this release: no app menu lists Forecast accuracy and no page links to it.' },
  'attributes': { exempt: 'Not offered in this release: no app menu lists Attribute learning and no page links to it.' },
  'governance': { exempt: 'Not offered in this release: no app menu lists Refresh and promotion and no page links to it.' },
  'promotions': {
    purposeKey: 'postseason.guide.promotions.purpose',
    readingKey: 'postseason.guide.promotions.reading',
    actionsKey: 'postseason.guide.promotions.actions',
    statesKey: 'postseason.guide.promotions.states',
    sourceKey: 'postseason.guide.promotions.source',
    tour: [
      { anchor: 'postseason-promotions-scope', titleKey: 'postseason.guide.promotions.tour.scope.title', bodyKey: 'postseason.guide.promotions.tour.scope.body' },
      { anchor: 'headline', titleKey: 'postseason.guide.promotions.tour.headline.title', bodyKey: 'postseason.guide.promotions.tour.headline.body' },
      { anchor: 'postseason-promotions-timeline', titleKey: 'postseason.guide.promotions.tour.timeline.title', bodyKey: 'postseason.guide.promotions.tour.timeline.body' },
    ],
  },
  'promotions/window': {
    purposeKey: 'postseason.guide.promotionWindow.purpose',
    readingKey: 'postseason.guide.promotionWindow.reading',
    actionsKey: 'postseason.guide.promotionWindow.actions',
    statesKey: 'postseason.guide.promotionWindow.states',
    sourceKey: 'postseason.guide.promotionWindow.source',
    tour: [
      { anchor: 'postseason-promotions-basis', titleKey: 'postseason.guide.promotionWindow.tour.basis.title', bodyKey: 'postseason.guide.promotionWindow.tour.basis.body' },
      { anchor: 'postseason-promotions-impacts', titleKey: 'postseason.guide.promotionWindow.tour.impacts.title', bodyKey: 'postseason.guide.promotionWindow.tour.impacts.body' },
      { anchor: 'postseason-promotions-perimeter', titleKey: 'postseason.guide.promotionWindow.tour.perimeter.title', bodyKey: 'postseason.guide.promotionWindow.tour.perimeter.body' },
      { anchor: 'postseason-promotions-weeks', titleKey: 'postseason.guide.promotionWindow.tour.weeks.title', bodyKey: 'postseason.guide.promotionWindow.tour.weeks.body' },
    ],
  },
  'promotions/window/stores': {
    purposeKey: 'postseason.guide.promotionStores.purpose',
    readingKey: 'postseason.guide.promotionStores.reading',
    actionsKey: 'postseason.guide.promotionStores.actions',
    statesKey: 'postseason.guide.promotionStores.states',
    sourceKey: 'postseason.guide.promotionStores.source',
    tour: [
      { anchor: 'postseason-promotions-basis', titleKey: 'postseason.guide.promotionStores.tour.basis.title', bodyKey: 'postseason.guide.promotionStores.tour.basis.body' },
      { anchor: 'constants-band', titleKey: 'postseason.guide.promotionStores.tour.constants.title', bodyKey: 'postseason.guide.promotionStores.tour.constants.body' },
      { anchor: 'postseason-promotions-stores', titleKey: 'postseason.guide.promotionStores.tour.table.title', bodyKey: 'postseason.guide.promotionStores.tour.table.body' },
    ],
  },
  'promotions/window/store': {
    purposeKey: 'postseason.guide.promotionStore.purpose',
    readingKey: 'postseason.guide.promotionStore.reading',
    actionsKey: 'postseason.guide.promotionStore.actions',
    statesKey: 'postseason.guide.promotionStore.states',
    sourceKey: 'postseason.guide.promotionStore.source',
    tour: [
      { anchor: 'postseason-promotions-basis', titleKey: 'postseason.guide.promotionStore.tour.basis.title', bodyKey: 'postseason.guide.promotionStore.tour.basis.body' },
      { anchor: 'constants-band', titleKey: 'postseason.guide.promotionStore.tour.constants.title', bodyKey: 'postseason.guide.promotionStore.tour.constants.body' },
      { anchor: 'postseason-promotions-store', titleKey: 'postseason.guide.promotionStore.tour.table.title', bodyKey: 'postseason.guide.promotionStore.tour.table.body' },
    ],
  },
  'promotions/window/articles': {
    purposeKey: 'postseason.guide.promotionArticles.purpose',
    readingKey: 'postseason.guide.promotionArticles.reading',
    actionsKey: 'postseason.guide.promotionArticles.actions',
    statesKey: 'postseason.guide.promotionArticles.states',
    sourceKey: 'postseason.guide.promotionArticles.source',
    tour: [
      { anchor: 'headline', titleKey: 'postseason.guide.promotionArticles.tour.headline.title', bodyKey: 'postseason.guide.promotionArticles.tour.headline.body' },
      { anchor: 'constants-band', titleKey: 'postseason.guide.promotionArticles.tour.constants.title', bodyKey: 'postseason.guide.promotionArticles.tour.constants.body' },
      { anchor: 'postseason-promotions-articles', titleKey: 'postseason.guide.promotionArticles.tour.table.title', bodyKey: 'postseason.guide.promotionArticles.tour.table.body' },
    ],
  },
  'promotions/mechanics': {
    purposeKey: 'postseason.guide.promotionMechanics.purpose',
    readingKey: 'postseason.guide.promotionMechanics.reading',
    actionsKey: 'postseason.guide.promotionMechanics.actions',
    statesKey: 'postseason.guide.promotionMechanics.states',
    sourceKey: 'postseason.guide.promotionMechanics.source',
    tour: [
      { anchor: 'postseason-promotions-scope', titleKey: 'postseason.guide.promotionMechanics.tour.scope.title', bodyKey: 'postseason.guide.promotionMechanics.tour.scope.body' },
      { anchor: 'postseason-promotions-basis', titleKey: 'postseason.guide.promotionMechanics.tour.basis.title', bodyKey: 'postseason.guide.promotionMechanics.tour.basis.body' },
      { anchor: 'postseason-promotions-chart', titleKey: 'postseason.guide.promotionMechanics.tour.chart.title', bodyKey: 'postseason.guide.promotionMechanics.tour.chart.body' },
      { anchor: 'postseason-promotions-mechanics-table', titleKey: 'postseason.guide.promotionMechanics.tour.table.title', bodyKey: 'postseason.guide.promotionMechanics.tour.table.body' },
      { anchor: 'postseason-promotions-response', titleKey: 'postseason.guide.promotionMechanics.tour.response.title', bodyKey: 'postseason.guide.promotionMechanics.tour.response.body' },
    ],
  },
  'suppliers': { exempt: 'Not offered in this release: no app menu lists Supplier scorecards and no page links to it.' },
};
